/**
 * Transaction Cancellation Request Screen
 *
 * Screen for initiating a transaction cancellation with reason selection
 * Shows transaction details and allows user to select cancellation reason
 */

import { t } from "@lingui/core/macro";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { TokenUSDC } from "@web3icons/react";
import { ArrowLeft, ArrowRightLeft, Receipt } from "lucide-react";
import { useEffect, useState } from "react";
import type { RefundCommitRequest, TransactionStatusResponse } from "@/api";
import { getCancellationReasons } from "@/constants/cancellation-reasons";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { useAnalytics } from "@/hooks/usePostHog";
import { getAuthTokenFromSearchParams } from "@/lib/ramps-search-schema";
import { toast } from "@/lib/toast";
import {
	hasCancellationBeenSubmitted,
	rampsActions,
	useRampsStore,
} from "@/stores/use-ramps-store";
import { ANALYTICS_EVENTS } from "@/utils/analytics";
import { captureException } from "@/utils/error-tracking";

import { RampsButton } from "../components/ramps/Button";
import { RampsSelectBottomSheet } from "../components/ramps/RampsSelectBottomSheet";
import { RampsText } from "../components/ramps/Text";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import { navigateWithRampsState } from "../lib/ramps-navigation";

// Helper function to format reference number
const getFormattedReferenceNumber = (referenceNumber?: string) => {
	if (!referenceNumber) return "";
	// Format as XXX XXX XXX for readability
	return referenceNumber
		.slice(0, 9)
		.replace(/(.{3})/g, "$1 ")
		.trim();
};

// Helper function to get display text for transaction type
const getTransactionTypeDisplay = (kind?: string) => {
	if (!kind) return t`Transaction`;
	const upperKind = kind.toUpperCase();
	switch (upperKind) {
		case "WITHDRAWAL":
			return t`Cash out`;
		case "DEPOSIT":
			return t`Cash In`;
		default:
			return kind;
	}
};

export const Route = createFileRoute("/ramps/cancel")({
	validateSearch: (search) =>
		search as {
			step?: string;
			transaction_id?: string;
			token?: string;
			accesstoken?: string;
			refNumber?: string;
			lang?: string;
			wallet_provider?: string;
		},
	component: CancellationRequestPage,
});

function CancellationRequestPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const search = Route.useSearch();
	const store = useRampsStore();
	// Reactive read so the submit button disables the moment the marker is
	// set, even before navigation completes.
	const alreadySubmittedForThisTx = useRampsStore((s) =>
		s.cancellationSubmittedTxIds.includes(
			search.transaction_id || s.transactionId,
		),
	);
	const { trackPageView, trackEvent } = useAnalytics();

	// Get auth token (supports both v3 and v5 API)
	const authToken = getAuthTokenFromSearchParams(search);

	// PostHog: page view when user lands on cancel page
	useEffect(() => {
		trackPageView("cancel", { page_name: "cancel" });
		trackEvent(ANALYTICS_EVENTS.PAGE_CANCEL_VIEWED, {
			transaction_id: search.transaction_id,
			wallet_provider: search.wallet_provider,
		});
	}, [
		trackPageView,
		trackEvent,
		search.transaction_id,
		search.wallet_provider,
	]);

	// Get transaction ID from URL or store (v3 may have it from prepare endpoint)
	const transactionId = search.transaction_id || store.transactionId;

	// IMPORTANT: Cancel page derives API version from transaction-status page
	// If store already has an API version (set by transaction-status), use it
	// This ensures v3 transactions stay v3 even if refNumber is in URL
	const { apiClient } = useApiClientWithAutoVersion({
		authToken: authToken || "",
		// Force use of store version if it exists (from transaction-status page)
		// The hook will preserve store version when token matches
	});
	const [selectedReason, setSelectedReason] = useState<string>("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	// Soft cooldown after a failed commit — prevents rapid-fire retries that
	// just flood Sentry/backend logs (HAR `q5ramps.aws.moneygram.com1.har`
	// showed 3 PUTs returning 500 in ~80 s). 5 s gives the user enough time
	// to read the toast without feeling stuck.
	const [isCoolingDown, setIsCoolingDown] = useState(false);

	// Fetch transaction data if coming from transaction-status page
	// For v3: refNumber is not in URL params, API doesn't require it
	// For v5: refNumber may be passed in URL params
	const { data: transactionData, isLoading: isLoadingTransaction } =
		useQuery<TransactionStatusResponse | null>({
			queryKey: ["transaction-data", transactionId, search.refNumber],
			queryFn: async () => {
				if (!transactionId || !authToken) return null;
				return await apiClient.transaction.getTransactionStatus(
					transactionId,
					search.refNumber, // Optional - v3 doesn't require it
					authToken,
				);
			},
			enabled: !!transactionId && !!authToken,
		});

	// Fetch refund info when reason is selected (for future use)
	const { data: refundInfo, isFetching: refundInfoIsFetching } = useQuery({
		queryKey: ["refund-info", transactionId, selectedReason],
		queryFn: async () => {
			if (!transactionId || !authToken || !selectedReason) return null;
			return await apiClient.transaction.getRefundInfo(
				transactionId,
				selectedReason,
				authToken,
			);
		},
		enabled: !!transactionId && !!authToken && !!selectedReason,
	});

	const handleBack = () => {
		// Build search params dynamically to include both v3 and v5 token params
		navigate({
			to: "/transaction-status",
			search: {
				transaction_id: transactionId || "",
				refNumber: search.refNumber,
				wallet_provider: store.walletProvider,
				lang: store.lang,
				token: search.token,
				accesstoken: search.accesstoken,
			},
		});
	};

	const handleReasonSelect = (reason: string) => {
		setSelectedReason(reason);
	};

	const handleCancelTransaction = async () => {
		if (!selectedReason || !transactionId || !authToken) return;
		// Race-safety: even if the disabled state didn't propagate yet, refuse
		// to fire a duplicate commit for a transactionId we've already cancelled.
		if (hasCancellationBeenSubmitted(transactionId)) return;

		setIsSubmitting(true);

		try {
			// Prepare refund commit request
			const refundRequest: RefundCommitRequest = {
				userLanguage: store.lang || "en-US",
				targetAudience: "CONSUMER_FACING",
				refundReasonCode:
					selectedReason as RefundCommitRequest["refundReasonCode"],
				sendAmount: {
					currencyCode:
						transactionData?.sendAmountDetails?.amount?.currencyCode || "USDC",
					value: transactionData?.sendAmountDetails?.amount?.value || 0,
				},
			};

			// Commit the refund
			await apiClient.transaction.commitRefund(
				transactionId,
				refundRequest,
				authToken,
			);

			// Mark this transactionId as cancelled in the session store so the
			// status page hides the Cancel button immediately, regardless of
			// whether the backend has propagated `isRefundAllowedFlag=false`
			// yet (observed propagation: 12s → many minutes).
			rampsActions.markCancellationSubmitted(transactionId);

			// Optimistically patch any cached transaction payloads so a navigation
			// back to /transaction-status shows the correct gate before the
			// refetch resolves. Both query keys are version-blind and match by
			// prefix across v2/v3/v5.
			queryClient.setQueriesData<TransactionStatusResponse | null>(
				{ queryKey: ["transaction-data", transactionId] },
				(prev) => (prev ? { ...prev, isRefundAllowedFlag: false } : prev),
			);
			queryClient.setQueriesData<TransactionStatusResponse | null>(
				{ queryKey: ["transaction-status", transactionId] },
				(prev) => (prev ? { ...prev, isRefundAllowedFlag: false } : prev),
			);

			// Invalidate to trigger background refetches — once the backend
			// catches up, the optimistic patch is overwritten by truth.
			void queryClient.invalidateQueries({
				queryKey: ["transaction-data", transactionId],
			});
			void queryClient.invalidateQueries({
				queryKey: ["transaction-status", transactionId],
			});

			// Navigate to success screen
			navigate(
				navigateWithRampsState(search, {
					to: "/ramps/cancel-success",
				}),
			);
		} catch (error) {
			// `transaction.service.ts#handleApiError` re-throws as a plain Error
			// with the HTTP status embedded in the message ("… (Status: 500)").
			// Parse it back out to drive status-aware UX + analytics.
			const message = error instanceof Error ? error.message : String(error);
			const statusMatch = message.match(/\(Status:\s*(\d+)\)/);
			const httpStatus = statusMatch ? Number(statusMatch[1]) : undefined;
			const isNetworkError = /Network error/i.test(message);

			captureException(error, {
				tags: {
					component: "CancelPage",
					action: "submitCancellation",
					http_status: httpStatus ? String(httpStatus) : "unknown",
				},
			});

			// PostHog: surface the failure so we can see it without a HAR.
			// Existing `PAGE_CANCEL_VIEWED` only covered the happy path.
			trackEvent(ANALYTICS_EVENTS.TRANSACTION_CANCEL_FAILED, {
				transaction_id: transactionId,
				wallet_provider: search.wallet_provider,
				refund_reason_code: selectedReason,
				http_status: httpStatus,
				is_network_error: isNetworkError,
				error_message: message,
			});

			// Status-aware copy so users can self-diagnose.
			let toastBody: string;
			if (isNetworkError) {
				toastBody = t`We couldn't reach our servers. Check your connection and try again.`;
			} else if (httpStatus && httpStatus >= 500) {
				toastBody = t`Our service is temporarily unavailable. Please try again in a moment.`;
			} else if (httpStatus && httpStatus >= 400) {
				toastBody = t`We couldn't process this cancellation. Please contact support if it keeps failing.`;
			} else {
				toastBody = t`We couldn't cancel this transfer right now. Please try again in a moment.`;
			}
			toast.apiError(toastBody, { header: t`Cancellation failed` });

			// Soft cooldown: hold the button disabled for 5 s after a failure
			// so repeated taps don't flood the backend with retries.
			setIsCoolingDown(true);
			setTimeout(() => setIsCoolingDown(false), 5000);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div
			className="flex min-h-dvh flex-col"
			style={{ backgroundColor: "var(--ramps-background-base)" }}
		>
			{/* Header */}
			<div className="flex items-center justify-between p-4">
				<button
					type="button"
					onClick={handleBack}
					className="flex h-8 w-8 items-center justify-center transition-opacity hover:opacity-80"
					aria-label="Back"
				>
					<ArrowLeft className="h-6 w-6 text-ramps-text-primary" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="mb-8 px-4">
				<Progress
					value={20}
					className="h-1 bg-ramps-background-surface-2 [&>*]:bg-ramps-text-primary"
				/>
			</div>

			{/* Content */}
			<div className="flex flex-1 flex-col px-4">
				<div className="mb-8 text-left">
					<RampsText variant="title-lg-bold" color="primary" className="mb-2">
						{t`Request to cancel transfer`}
					</RampsText>
				</div>

				{/* Transaction Details */}
				<div className="mb-8 space-y-6">
					{/* Reference Number */}
					<div className="flex items-center gap-4">
						<div className="p-3">
							<Receipt size={20} />
						</div>
						<div>
							<RampsText variant="body-sm" color="secondary" className="mb-1">
								{t`Reference #`}
							</RampsText>
							{transactionData?.referenceNumber ? (
								<RampsText variant="body-default">
									{getFormattedReferenceNumber(transactionData.referenceNumber)}
								</RampsText>
							) : (
								<Skeleton className="h-6 w-24" />
							)}
						</div>
					</div>

					{/* Transfer Amount */}
					<div className="flex items-center gap-4">
						<TokenUSDC className="ml-3 h-7 w-7" />
						<div>
							<RampsText variant="body-sm" color="secondary" className="mb-1">
								{t`Transfer amount`}
							</RampsText>
							{refundInfo?.totalRefundAmount ||
							transactionData?.sendAmountDetails?.totalAmount ? (
								<RampsText variant="body-default">
									{refundInfo?.totalRefundAmount
										? `${refundInfo.totalRefundAmount.value.toFixed(2)} ${refundInfo.totalRefundAmount.currencyCode}`
										: `${transactionData?.sendAmountDetails?.totalAmount?.value.toFixed(2)} ${transactionData?.sendAmountDetails?.totalAmount?.currencyCode}`}
								</RampsText>
							) : (
								<Skeleton className="h-6 w-32" />
							)}
						</div>
					</div>

					{/* Type */}
					<div className="flex items-center gap-4">
						<div className="rounded-lg p-3">
							<ArrowRightLeft
								size={20}
								style={{ color: "var(--ramps-text)" }}
							/>
						</div>
						<div>
							<RampsText variant="body-sm" color="secondary" className="mb-1">
								{t`Type`}
							</RampsText>
							{transactionData?.kind ? (
								<RampsText variant="body-default">
									{getTransactionTypeDisplay(transactionData.kind)}
								</RampsText>
							) : (
								<Skeleton className="h-6 w-20" />
							)}
						</div>
					</div>
				</div>

				{/* Reason Selection */}
				<div className="mb-8">
					<RampsSelectBottomSheet
						label={t`Reason`}
						sheetTitle={t`Select reason`}
						options={getCancellationReasons().map((reason) => ({
							value: reason.value,
							label: reason.description,
						}))}
						value={selectedReason}
						onSelect={(value) => handleReasonSelect(value)}
						size="compact"
						placeholder={t`Select reason`}
					/>
				</div>
			</div>

			{/* Bottom Button */}
			<div className="p-4">
				<RampsButton
					variant="primary"
					size="large"
					className={`w-full ${
						isCoolingDown || alreadySubmittedForThisTx
							? "cursor-not-allowed"
							: ""
					}`}
					onClick={handleCancelTransaction}
					disabled={
						!selectedReason ||
						isSubmitting ||
						isCoolingDown ||
						!transactionData ||
						isLoadingTransaction ||
						refundInfoIsFetching ||
						alreadySubmittedForThisTx
					}
					loading={isSubmitting || refundInfoIsFetching}
				>
					{alreadySubmittedForThisTx
						? t`Cancellation submitted`
						: isCoolingDown
							? t`Please wait…`
							: t`Cancel transaction`}
				</RampsButton>
			</div>
		</div>
	);
}
